import React, { useEffect, useState } from "react";
import {
  Box, HStack, VStack, Text, Badge, Alert, AlertIcon, Button, Code, Link, Select, Collapse, Spinner,
} from "@chakra-ui/react";
import { AiFindingCard, nightLabel } from "./AiFindings";

export { aiFindingTaskText } from "./AiFindings";

/**
 * The Code Review tab's nightly AI review, written by the nightly-code-review cron job
 * (backend/api/code_review/nightly.py) just after midnight in New York. The open findings of every
 * night go above the night-by-night history (the `children`); the history says what each night
 * reviewed, found and saw fixed. Memory and database findings are filed in their own tabs, so a
 * night only counts them here. Without the findings table (its migration not run yet) each
 * night's findings are listed here instead.
 */

function plural(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

const ELSEWHERE = { memory: "in Memory Issues", database: "in DB Health Check" };

function Summary({ run }) {
  const commits = run.commits || [];
  const findings = run.findings || [];
  const fixed = run.fixed || [];
  if (run.status === "no_commits") return <Text fontSize="sm" color="gray.600">No commits were pushed that day.</Text>;
  const problems = findings.filter((f) => f.severity === "problem").length;
  return (
    <Text fontSize="sm" color="gray.700" data-testid="code-review-night-summary">
      {plural(commits.length, "commit")} reviewed
      {findings.length
        ? ` · ${plural(findings.length, "finding")}${problems ? ` (${plural(problems, "problem")})` : ""}`
        : " · no problems found"}
      {fixed.length ? ` · ${plural(fixed.length, "earlier finding")} fixed` : ""}
      {run.ran_at ? ` · ran ${new Date(run.ran_at).toLocaleString()}` : ""}
    </Text>
  );
}

/** Where that night's findings went: "2 here · 1 in Memory Issues". */
function FiledIn({ run }) {
  const findings = run.findings || [];
  if (!findings.length) return null;
  const count = (area) => findings.filter((f) => (f.area || "general") === area).length;
  const parts = [count("general") && `${count("general")} here`,
                 ...Object.entries(ELSEWHERE).map(([area, where]) => count(area) && `${count(area)} ${where}`)]
    .filter(Boolean);
  return <Text fontSize="xs" color="gray.600" data-testid="code-review-night-filed">Found that night: {parts.join(" · ")}</Text>;
}

function CommitList({ run }) {
  const [open, setOpen] = useState(false);
  const commits = run.commits || [];
  const skipped = commits.reduce((n, c) => n + (c.skipped?.length || 0), 0);
  if (!commits.length) return null;
  return (
    <Box>
      <Button size="xs" variant="link" colorScheme="blue" onClick={() => setOpen((o) => !o)}>
        {open ? "Hide the commits" : `Show the ${plural(commits.length, "commit")} reviewed`}
      </Button>
      <Collapse in={open} animateOpacity unmountOnExit>
        <VStack align="stretch" spacing={0.5} mt={2} p={2} bg="gray.50" borderRadius="md" fontSize="xs">
          {commits.map((c) => (
            <Text key={c.sha}>
              {c.url ? <Link href={c.url} isExternal color="blue.600">{c.short}</Link> : <Code fontSize="xs">{c.short}</Code>}
              {" "}{c.owner || c.author}: {c.message}
              {c.error ? <Text as="span" color="red.600"> ({c.error})</Text> : null}
            </Text>
          ))}
          {skipped > 0 && (
            <Text color="gray.600" mt={1}>
              {plural(skipped, "file")} weren't sent (data, generated or binary files never leave the server).
            </Text>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
}

export default function NightlyReview({ load, reloadKey = 0, showFindings = true, taskByRef = {}, canAssign, onAssign,
                                        TaskChip, children }) {
  const [state, setState] = useState({ loading: true });
  const [night, setNight] = useState(null);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((data) => { if (!cancelled) setState({ data }); })
      .catch((e) => { if (!cancelled) setState({ error: e?.message || String(e) }); });
    return () => { cancelled = true; };
  }, [load, reloadKey]);

  const runs = state.data?.runs || [];
  const run = runs.find((r) => r.review_date === night) || runs[0] || null;

  return (
    <Box data-testid="code-review-nightly">
      <Box mb={2}>
        <Text fontWeight="bold">Nightly AI review of the day's commits</Text>
        <Text fontSize="xs" color="gray.600">
          Claude reviews the commits pushed each day (by itself just after midnight, or now with Review new commits)
          for bugs, security and privacy problems, memory and database problems. Only source code is sent, with keys
          blanked out. Memory findings go to the Memory Issues tab and database ones to the DB Health Check tab; the
          rest stay here until a commit fixes them or someone dismisses them.
        </Text>
      </Box>

      {children}

      <HStack justify="space-between" flexWrap="wrap" spacing={3} mt={children ? 4 : 0} mb={2}>
        <Text fontSize="sm" fontWeight="bold" color="gray.600">Night by night</Text>
        {runs.length > 1 && (
          <Select size="sm" w="auto" value={run?.review_date || ""} onChange={(e) => setNight(e.target.value)}
                  data-testid="code-review-night" aria-label="Which night">
            {runs.map((r) => <option key={r.review_date} value={r.review_date}>{nightLabel(r.review_date)}</option>)}
          </Select>
        )}
      </HStack>

      {state.loading && <Spinner size="sm" />}
      {state.error && <Alert status="error" borderRadius="md"><AlertIcon />Couldn't load the nightly reviews: {state.error}</Alert>}
      {state.data?.missing && <Alert status="info" borderRadius="md"><AlertIcon />{state.data.missing}</Alert>}
      {state.data && !state.data.missing && !run && (
        <Text fontSize="sm" color="gray.600">No night has been reviewed yet: the first review runs tonight at midnight.</Text>
      )}

      {run && (
        <VStack align="stretch" spacing={3}>
          <HStack spacing={2} flexWrap="wrap">
            <Badge>{nightLabel(run.review_date)}</Badge>
            <Summary run={run} />
          </HStack>
          {run.status === "failed" && (
            <Alert status="warning" borderRadius="md"><AlertIcon />The review didn't finish that night: {run.error}</Alert>
          )}
          {showFindings
            ? (run.findings || []).map((f) => (
              <AiFindingCard key={f.id} finding={f} task={taskByRef[f.id]} canAssign={canAssign}
                             onAssign={onAssign ? (finding) => onAssign(finding, run) : null} TaskChip={TaskChip} />
            ))
            : <FiledIn run={run} />}
          {(run.not_reviewed || []).length > 0 && (
            <Text fontSize="xs" color="gray.600">
              {plural(run.not_reviewed.length, "commit")} will be reviewed the next night
              ({run.not_reviewed[0].error}).
            </Text>
          )}
          <CommitList run={run} />
        </VStack>
      )}
    </Box>
  );
}
